import Chart from 'react-apexcharts';
import {
  usePeakAverageRoadRoughness,
} from '@src/modules/performance/context/reports-api-hooks';
import { selectedDatabaseIdState } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import { Text } from '@chakra-ui/react';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Road Roughness (Sealed Roads)',
  content: [
    {
      title: 'Peak and average road roughness (NAASRA)',
      description:
  <>
    <Text fontSize="inherit" mb={2}>The 75th and 85th percentiles and average roughness for the sealed road network each year reported in NAASRA counts/km. Higher results reflect a more rough road network.</Text>
    <Text fontSize="inherit" mb={2}>The average result for the peer group is shown in a lighter shade of the same colour, and the national average in black to provide context.</Text>
    <Text fontSize="inherit" mb={2}>Road Efficiency Group Te Ringa Maimoa has calculated these results</Text>
  </>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from Road Efficiency Group Te Ringa Maimoa ONRC Performance Measure Reporting.
  </Text>,
    },
  ],
};

function RoadConditionRoadRoughness() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const { data: peakAverageRoadRoughness, isLoading } = usePeakAverageRoadRoughness();
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;

  // Peak and Average Road Roughness Chart
  const peakAverageRoadRoughnessYears = [...new Set(peakAverageRoadRoughness?.years?.flat())];
  const peakAverageRoadRoughnessData = [{
    name: '75th Percentile',
    data: peakAverageRoadRoughness?.values?.[0]?.map((value, index) => ({ x: peakAverageRoadRoughnessYears[index], y: value })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer 75th',
    data: peakAverageRoadRoughness?.values?.[1]?.map((value, index) => ({ x: peakAverageRoadRoughnessYears[index], y: value })),
    color: CHART_COLOURS.red,
  }, {
    name: '85th Percentile',
    data: peakAverageRoadRoughness?.values?.[2]?.map((value, index) => ({ x: peakAverageRoadRoughnessYears[index], y: value })),
    color: CHART_COLOURS.orange,
  },
  {
    name: 'Peer 85th',
    data: peakAverageRoadRoughness?.values?.[3]?.map((value, index) => ({ x: peakAverageRoadRoughnessYears[index], y: value })),
    color: CHART_COLOURS.seagreen,
  }, {
    name: 'Average',
    data: peakAverageRoadRoughness?.values?.[4]?.map((value, index) => ({ x: peakAverageRoadRoughnessYears[index], y: value })),
    color: CHART_COLOURS.magenta,
  }, {
    name: 'Peer Average',
    data: peakAverageRoadRoughness?.values?.[5]?.map((value, index) => ({ x: peakAverageRoadRoughnessYears[index], y: value })),
    color: CHART_COLOURS.grey,
  }, {
    name: 'National Average',
    data: peakAverageRoadRoughness?.values?.[6]?.map((value, index) => ({ x: peakAverageRoadRoughnessYears[index], y: value })),
    color: CHART_COLOURS.black,
  }];

  const peakAverageRoadRoughnessChartSeries = isRegionNationalOrStateHighways
    ? [...peakAverageRoadRoughnessData.filter((x) => !x.name?.includes('Peer'))]
    : [...peakAverageRoadRoughnessData];

  const peakAverageRoadRoughnessValues = [...new Set(
    peakAverageRoadRoughnessChartSeries?.flatMap((x) => x.data?.flatMap((y) => (Array.isArray(y.y) ? y.y : [y.y]))),
  )];

  return (
    <ReportSection
      title="Road Roughness (Sealed Roads)"
      isLoading={isLoading || !peakAverageRoadRoughness}
      lockYear={peakAverageRoadRoughness?.lockYear}
      informationSource={informationSource}
    >
      <Chart
        options={
            {
              title: {
                text: 'Peak and average road roughness (NAASRA)',
              },
              chart: {
                animations: {
                  enabled: false,
                },
                // Hide series on initial load
                // Note: There is a bug when using hidden: true that hides the markers, so we use the mounted event to hide the series instead
                events: {
                  mounted: (chart) => {
                    chart.hideSeries('85th Percentile');
                    chart.hideSeries('Peer 85th');
                    chart.hideSeries('Average');
                    chart.hideSeries('Peer Average');
                    chart.hideSeries('National Average');
                  },
                },
              },
              xaxis: {
                categories: peakAverageRoadRoughnessYears,
                labels: {
                  offsetY: 5,
                  rotate: -45,
                  rotateAlways: true,
                },
              },
              yaxis: [
                {
                  forceNiceScale: true,
                  min: Math.min(...peakAverageRoadRoughnessValues),
                  max: Math.max(...peakAverageRoadRoughnessValues),
                  labels: {
                    formatter(y) {
                      return `${y.toFixed(0)}`;
                    },
                  },
                },
              ],
              grid: {
                padding: {
                  left: 20,
                },
              },
              dataLabels: {
                enabled: false,
              },
              stroke: {
                curve: 'straight',
                width: peakAverageRoadRoughnessYears.length > 1 ? 2 : 0,
              },
              markers: {
                size: 3,
                hover: {
                  size: 6,
                },
              },
              tooltip: {
                shared: false,
                intersect: true,
              },
            }
          }
        series={peakAverageRoadRoughnessChartSeries}
        type="line"
        width="100%"
        height="280"
      />
    </ReportSection>
  );
}

export default RoadConditionRoadRoughness;
